import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { getThemeFileUri } from '../utils/wp.js';

function createPin(pin) {
    const element = document.createElement('img');
    if (pin.type === 'red') {
        element.src = getThemeFileUri('static/images/marker-hotel-purple.svg');
    } else if (pin.type === 'blue') {
        element.src = getThemeFileUri('static/images/marker-event-purple.svg');
    } else if (pin.type === 'green') {
        element.src = getThemeFileUri('static/images/greenmarker.png');
    }

    element.style.width = '36px';
    element.style.height = '58px';
    element.style.cursor = 'pointer';
    element.style.filter =
        'drop-shadow(1px 1px 0 rgba(255, 255, 255, 0.8)) drop-shadow(-1px -1px 0 rgba(255, 255, 255, 0.8))';

    return new mapboxgl.Marker({
        element: element,
    })
        .setLngLat([pin.lng, pin.lat]);
}

function createPinPopup(pin) {
    const element = document.createElement('div');
    element.style.maxWidth = '250px';
    element.style.padding = '8px 15px 0 15px';
    element.innerHTML =
        '<p style="margin-bottom: 0;">' + pin.popup + '</p>';

    return new mapboxgl.Popup({
        anchor: 'center',
        offset: [0, -60],
    })
        .setLngLat([pin.lng, pin.lat])
        .setDOMContent(element);
}

function Map({
    center,
    pins,
    zoom = 12,
    interactive = false,
    scale = true,
    onClick,
}) {
    const ref = useRef(null);
    const data = useRef({map: null, pins: []});

    useLayoutEffect(() => {
        if (!ref.current) {
            return;
        }

        if (!data.current.map) {
            data.current.map = new mapboxgl.Map({
                container: ref.current,
                style: 'mapbox://styles/mapbox/streets-v12',
                center: [center.lng, center.lat],
                zoom: zoom,
                interactive: interactive,
            });

            if (scale) {
                data.current.map.addControl(
                    new mapboxgl.ScaleControl(),
                    'bottom-right',
                );
            }

            data.current.map.addControl(
                new mapboxgl.NavigationControl({
                    visualizePitch: false,
                    showCompass: false,
                }), 'bottom-right');
        }

        for (let i = 0, l = data.current.pins.length; i < l; ++i) {
            data.current.pins[i].remove();
        }

        for (let i = 0, l = pins.length, pin; i < l; ++i) {
            pin = pins[i];

            if (pins[i].lng == null || pins[i].lat == null) {
                continue;
            }

            pin = createPin(pins[i]);
            if (pins[i].popup != null) {
                pin.setPopup(createPinPopup(pins[i]));
            }

            pin.addTo(data.current.map);
            data.current.pins.push(pin);
        }

        const coords = pins.map((pin) => {
            if (pin.lng == null || pin.lat == null) {
                return false;
            }

            return [pin.lng, pin.lat];
        }).filter(Boolean);

        if (coords.length > 1) {
            const bounds = coords.reduce(
                (bounds, coordinate) => bounds.extend(coordinate),
                new mapboxgl.LngLatBounds(coords[0], coords[0]),
            );

            try {
                data.current.map.fitBounds(bounds, {padding: 36});
            } catch (ex) {
                // Too small for the padding, remove the padding and continue.
                data.current.map.fitBounds(bounds);
            }
        } else if (coords.length === 1) {
            data.current.map.flyTo({center: coords[0]});
        }
    }, [ref, pins]);

    useEffect(() => {
        return () => {
            data.current.pins = [];
            if (data.current.map) {
                data.current.map.remove();
                data.current.map = null;
            }
        };
    }, []);

    return <div
        className="w-full h-full min-h-[200px]"
        ref={ref}
        onClick={onClick}
    />;
}

export { Map };
